/**
 * SessionSummaryButton
 *
 * Reabre o resumo pós-treino (WorkoutSummaryModal) de uma sessão já finalizada.
 * O resumo é remontado pelo servidor (GET /workout-sessions/{id}/summary), então
 * funciona depois de fechar o app e também pro professor vendo o aluno.
 *
 * Props:
 *   sessionId – id da sessão finalizada
 *   variant   – "button" (padrão, só o botão) | "card" (linha "Último treino")
 *   title     – (card) nome do treino
 *   subtitle  – (card) data/duração
 */
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlagCheckered } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import WorkoutSummaryModal from "./WorkoutSummaryModal";
import styles from "./SessionSummaryButton.module.css";

export default function SessionSummaryButton({ sessionId, variant = "button", title, subtitle }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!sessionId) return null;

  const open = async (e) => {
    e?.stopPropagation();
    if (loading) return;
    setLoading(true);
    setFailed(false);
    try {
      const res = await api.get(`/workout-sessions/${sessionId}/summary`);
      setSummary(res.data);
    } catch (_) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const button = (
    <button type="button" className={styles.btn} onClick={open} disabled={loading}>
      <FontAwesomeIcon icon={faFlagCheckered} aria-hidden="true" />
      {loading ? "Abrindo..." : "Ver resumo"}
    </button>
  );

  return (
    <>
      {variant === "card" ? (
        <div className={styles.card}>
          <div className={styles.cardText}>
            <span className={styles.cardLabel}>Último treino</span>
            <strong className={styles.cardTitle}>{title}</strong>
            {subtitle && <span className={styles.cardSub}>{subtitle}</span>}
          </div>
          {button}
        </div>
      ) : button}
      {failed && (
        <span className={styles.error} role="alert">Não foi possível abrir o resumo. Tente de novo.</span>
      )}
      {/* O modal é um portal, mas os eventos de React sobem pela árvore: sem
          este wrapper, clicar fora do resumo fecharia também o modal pai
          (ex.: SessionDetailModal). */}
      <div style={{ display: "contents" }} onClick={(e) => e.stopPropagation()}>
        <WorkoutSummaryModal summary={summary} onClose={() => setSummary(null)} />
      </div>
    </>
  );
}
